import Swiper from "swiper";
import {
  Navigation,
  Pagination,
  Keyboard,
  Zoom,
  Thumbs,
  FreeMode,
  Scrollbar,
  Mousewheel,
  A11y
} from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";
import "swiper/css/thumbs";
import "swiper/css/free-mode";
import "swiper/css/scrollbar";

const SWIPER_MODULES = [Navigation, Pagination, Keyboard, Zoom, Thumbs, A11y];

class Gallery {
  constructor() {
    this.lightbox = null;
    this.lightboxSwiper = null;
    this.init();
  }

  init() {
    document.querySelectorAll("[data-gallery]").forEach(gallery => {
      if (gallery.dataset.galleryInit) {
        return;
      }
      gallery.dataset.galleryInit = "true";

      if (gallery.dataset.galleryMode === "carousel") {
        this.initCarousel(gallery);
      } else {
        this.initLightbox(gallery);
      }
    });
  }

  initCarousel(gallery) {
    const el = gallery.querySelector(".gallery__swiper");
    if (!el) {
      return;
    }

    let thumbs = null;
    const thumbsEl = gallery.querySelector(".gallery__thumbs");
    if (thumbsEl) {
      thumbs = new Swiper(thumbsEl, {
        modules: [Thumbs, FreeMode, Scrollbar, Mousewheel, A11y],
        slidesPerView: "auto",
        spaceBetween: 8,
        watchSlidesProgress: true,
        freeMode: true,
        mousewheel: { forceToAxis: true },
        scrollbar: {
          el: gallery.querySelector(".gallery__thumbs-scrollbar"),
          draggable: true,
          hide: false
        }
      });
    }

    this.createSwiper(el, { thumbs });
  }

  initLightbox(gallery) {
    const thumbs = Array.from(gallery.querySelectorAll(".gallery__thumb"));
    thumbs.forEach(thumb => {
      thumb.addEventListener("click", () => {
        this.openLightbox(thumbs, Number(thumb.dataset.galleryIndex));
      });
    });
  }

  createSwiper(el, { initialSlide = 0, paginationType = "bullets", thumbs = null } = {}) {
    return new Swiper(el, {
      modules: SWIPER_MODULES,
      slidesPerView: 1,
      spaceBetween: 16,
      zoom: true,
      initialSlide,
      keyboard: { enabled: true },
      navigation: {
        nextEl: el.querySelector(".swiper-button-next"),
        prevEl: el.querySelector(".swiper-button-prev")
      },
      pagination: {
        el: el.querySelector(".swiper-pagination"),
        type: paginationType,
        clickable: true
      },
      thumbs: { swiper: thumbs }
    });
  }

  ensureLightbox() {
    if (this.lightbox) {
      return;
    }
    const overlay = document.createElement("div");
    overlay.className =
      "gallery-lightbox hidden fixed inset-0 z-[2000] items-center justify-center p-8 bg-black/90";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.innerHTML = `
      <button type="button" class="gallery-lightbox__close absolute top-4 right-4 z-10 w-10 h-10 p-0 border-0 rounded-full bg-black/50 hover:bg-black/80 text-white text-[1.75rem] leading-none cursor-pointer" aria-label="Close">&times;</button>
      <div class="swiper gallery-lightbox__swiper w-full h-full">
        <div class="swiper-wrapper"></div>
        <div class="swiper-button-prev"></div>
        <div class="swiper-button-next"></div>
        <div class="swiper-pagination"></div>
      </div>
    `;
    document.body.appendChild(overlay);
    this.lightbox = overlay;

    overlay
      .querySelector(".gallery-lightbox__close")
      .addEventListener("click", () => this.closeLightbox());
    overlay.addEventListener("click", event => {
      if (event.target === overlay) {
        this.closeLightbox();
      }
    });
    document.addEventListener("keydown", event => {
      if (
        event.key === "Escape" &&
        this.lightbox &&
        !this.lightbox.classList.contains("hidden")
      ) {
        this.closeLightbox();
      }
    });
  }

  openLightbox(thumbs, startIndex) {
    this.ensureLightbox();

    // Build slides with the DOM API rather than innerHTML so editor-supplied
    // captions and alt text can't inject markup.
    const slides = thumbs.map(thumb => {
      const slide = document.createElement("div");
      slide.className =
        "swiper-slide gallery-lightbox__slide relative flex flex-col items-center justify-center h-full";

      const zoom = document.createElement("div");
      zoom.className =
        "swiper-zoom-container flex-1 min-h-0 w-full flex items-center justify-center";
      const img = document.createElement("img");
      img.className = "max-w-full max-h-full w-auto object-contain";
      img.src = thumb.dataset.galleryFull;
      const thumbImg = thumb.querySelector("img");
      img.alt = thumbImg ? thumbImg.alt : "";
      zoom.appendChild(img);
      slide.appendChild(zoom);

      if (thumb.dataset.galleryCaption) {
        const caption = document.createElement("div");
        caption.className =
          "gallery__caption absolute left-1/2 -translate-x-1/2 bottom-12 z-[2] w-fit max-w-[calc(100%_-_2rem)] px-[0.9rem] py-[0.4rem] rounded-full bg-white/90 text-[#696969] text-center shadow-[0_1px_4px_rgba(0,0,0,0.25)]";
        caption.textContent = thumb.dataset.galleryCaption;
        slide.appendChild(caption);
      }

      return slide;
    });

    this.lightbox
      .querySelector(".swiper-wrapper")
      .replaceChildren(...slides);

    this.lightbox.classList.remove("hidden");
    this.lightbox.classList.add("flex");
    document.body.classList.add("gallery-lightbox-open");

    if (this.lightboxSwiper) {
      this.lightboxSwiper.destroy(true, true);
    }
    this.lightboxSwiper = this.createSwiper(
      this.lightbox.querySelector(".swiper"),
      { initialSlide: startIndex, paginationType: "fraction" }
    );
  }

  closeLightbox() {
    if (!this.lightbox) {
      return;
    }
    this.lightbox.classList.add("hidden");
    this.lightbox.classList.remove("flex");
    document.body.classList.remove("gallery-lightbox-open");
    if (this.lightboxSwiper) {
      this.lightboxSwiper.destroy(true, true);
      this.lightboxSwiper = null;
    }
  }
}

export default Gallery;
